import React, { useState } from "react";
import CustomToggleButton from "../../components/customToggleButton/customToggleButton";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import {
  blockWheelChangeOnNumberInput,
  manualAvailabilityButtonLabel,
  parsePriceFromPerson,
  toSoldOutFlag,
} from "./tableBookingUtils";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  Row,
} from "react-bootstrap";

/**
 * Right-panel zone (area) details + nested tables + optional focusBounds.
 */
const AreaInspectorFields = ({
  t,
  index,
  area: a,
  updateArea,
  onHotspotChange,
  showHotspotNumbers,
  setShowHotspotNumbers,
  focusTableIndex,
  onFocusTableIndex,
  updateTable,
  addTable,
  removeTable,
  outlineDrawMode = false,
  outlinePointCount = 0,
  onStartOutlineDraw,
  onClearOutline,
  onExpandMap,
  hasSceneImage = false,
  fieldErrors = {},
  onClearFieldError,
  expandedTableAccordion = null,
  onExpandedTableAccordionChange,
}) => {
  const [pendingChannelChange, setPendingChannelChange] = useState(null);

  if (!a) {
    return (
      <p className="small text-muted mb-0 py-3">
        {t("TABLE_BOOKING_STUDIO_NO_AREA")}
      </p>
    );
  }

  const idx = index;
  const tables = Array.isArray(a.tables) ? a.tables : [];
  const hasTables = tables.length > 0;
  const fieldId = (suffix) => `area-${idx}-${suffix}`;
  const tableFieldId = (ti, suffix) => `area-${idx}-table-${ti}-${suffix}`;
  const isInvalid = (id) => Boolean(fieldErrors?.[id]);
  const clearOnChange = (id, handler) => (e) => {
    onClearFieldError?.(id);
    handler(e);
  };

  const requestChannelSoldOutChange = (next, apply) => {
    const current = Boolean(next.current);
    const checked = Boolean(next.checked);
    if (current === checked) return;
    setPendingChannelChange({
      field: next.field,
      checked,
      apply,
    });
  };

  const closeChannelConfirm = () => setPendingChannelChange(null);

  const confirmChannelChange = () => {
    if (!pendingChannelChange) return;
    pendingChannelChange.apply(pendingChannelChange.checked);
    setPendingChannelChange(null);
  };

  const channelConfirmLabel =
    pendingChannelChange?.field === "offlineSoldOut"
      ? t("TABLE_BOOKING_OFFLINE_SOLD_OUT")
      : t("TABLE_BOOKING_ONLINE_SOLD_OUT");
  const channelConfirmState = pendingChannelChange?.checked
    ? t("TABLE_BOOKING_STATE_ON")
    : t("TABLE_BOOKING_STATE_OFF");

  return (
    <>
    <Row className="g-2">
      <Col xs={12}>
        <Alert variant="secondary" className="py-2 small mb-2 table-booking-studio-hint">
          {t("TABLE_BOOKING_CHECKOUT_AREA_ID_HINT")}
        </Alert>
      </Col>
      {isInvalid(fieldId("hotspot")) ? (
        <Col xs={12}>
          <Alert
            variant="danger"
            className="py-2 small mb-0 table-booking-field-error-group"
            data-field-id={fieldId("hotspot")}
          >
            {fieldErrors[fieldId("hotspot")]}
          </Alert>
        </Col>
      ) : null}
      <Col xs={12}>
        <Form.Group
          className={
            isInvalid(fieldId("label")) ? "table-booking-field-error-group" : ""
          }
        >
          <Form.Label>
            {t("TABLE_BOOKING_AREA_LABEL")}{" "}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            size="sm"
            data-field-id={fieldId("label")}
            isInvalid={isInvalid(fieldId("label"))}
            value={a.label}
            onChange={clearOnChange(fieldId("label"), (e) =>
              updateArea(idx, { label: e.target.value })
            )}
          />
          <Form.Control.Feedback type="invalid">
            {fieldErrors[fieldId("label")]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{t("TABLE_BOOKING_AREA_CODE")}</Form.Label>
          <Form.Control
            size="sm"
            value={a.code}
            onChange={(e) => updateArea(idx, { code: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Label className="d-block mb-1">
          {t("TABLE_BOOKING_MANUAL_AVAIL")}
        </Form.Label>
        <div className="d-flex flex-wrap gap-1">
          {["AUTO", "SOLD_OUT", "UNAVAILABLE"].map((k) => (
            <button
              key={k}
              type="button"
              className={`btn btn-sm ${
                String(a.manualAvailability).toUpperCase() === k
                  ? "btn-studio-primary"
                  : "btn-studio-secondary"
              }`}
              onClick={() => updateArea(idx, { manualAvailability: k })}
            >
              {manualAvailabilityButtonLabel(k, t)}
            </button>
          ))}
        </div>
      </Col>
      <Col xs={12}>
        <Form.Label className="d-block mb-1">
          {t("TABLE_BOOKING_ONLINE_SOLD_OUT")}
        </Form.Label>
        <div className="d-flex align-items-center gap-2">
          <CustomToggleButton
            id={fieldId("onlineSoldOut")}
            checked={toSoldOutFlag(a.onlineSoldOut)}
            onCheckChange={(checked) =>
              requestChannelSoldOutChange(
                {
                  field: "onlineSoldOut",
                  current: toSoldOutFlag(a.onlineSoldOut),
                  checked,
                },
                (nextChecked) =>
                  updateArea(idx, { onlineSoldOut: Boolean(nextChecked) })
              )
            }
          />
        </div>
        <Form.Text className="d-block">
          {t("TABLE_BOOKING_ONLINE_SOLD_OUT_HINT")}
        </Form.Text>
      </Col>
      <Col xs={12}>
        <Form.Label className="d-block mb-1">
          {t("TABLE_BOOKING_OFFLINE_SOLD_OUT")}
        </Form.Label>
        <div className="d-flex align-items-center gap-2">
          <CustomToggleButton
            id={fieldId("offlineSoldOut")}
            checked={toSoldOutFlag(a.offlineSoldOut)}
            onCheckChange={(checked) =>
              requestChannelSoldOutChange(
                {
                  field: "offlineSoldOut",
                  current: toSoldOutFlag(a.offlineSoldOut),
                  checked,
                },
                (nextChecked) =>
                  updateArea(idx, { offlineSoldOut: Boolean(nextChecked) })
              )
            }
          />
        </div>
        <Form.Text className="d-block">
          {t("TABLE_BOOKING_OFFLINE_SOLD_OUT_HINT")}
        </Form.Text>
      </Col>
      <Col xs={6}>
        <Form.Group
          className={
            isInvalid(fieldId("priceFromPerPerson"))
              ? "table-booking-field-error-group"
              : ""
          }
        >
          <Form.Label>
            {t("TABLE_BOOKING_PRICE_FROM_PERSON")}{" "}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            data-field-id={fieldId("priceFromPerPerson")}
            isInvalid={isInvalid(fieldId("priceFromPerPerson"))}
            value={a.priceFromPerPerson}
            placeholder={t("TABLE_BOOKING_PRICE_REQUIRED_PLACEHOLDER")}
            onWheel={blockWheelChangeOnNumberInput}
            onChange={clearOnChange(fieldId("priceFromPerPerson"), (e) =>
              updateArea(idx, {
                priceFromPerPerson: e.target.value,
              })
            )}
            onBlur={(e) => {
              const raw = e.target.value;
              updateArea(idx, {
                priceFromPerPerson:
                  raw === "" || raw == null
                    ? ""
                    : parsePriceFromPerson(raw),
              });
            }}
          />
          <Form.Control.Feedback type="invalid">
            {fieldErrors[fieldId("priceFromPerPerson")]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={6}>
        <Form.Group>
          <Form.Label>{t("TABLE_BOOKING_CURRENCY")}</Form.Label>
          <Form.Control
            size="sm"
            value={a.currency}
            onChange={(e) => updateArea(idx, { currency: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{t("TABLE_BOOKING_PRICE_DISPLAY")}</Form.Label>
          <Form.Control
            size="sm"
            value={a.priceDisplayLabel}
            onChange={(e) =>
              updateArea(idx, {
                priceDisplayLabel: e.target.value,
              })
            }
          />
        </Form.Group>
      </Col>
      <Col xs={4}>
        <Form.Group
          className={
            isInvalid(fieldId("totalTableSlots"))
              ? "table-booking-field-error-group"
              : ""
          }
        >
          <Form.Label>{t("TABLE_BOOKING_TABLE_POOL")}</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={1}
            data-field-id={fieldId("totalTableSlots")}
            isInvalid={isInvalid(fieldId("totalTableSlots"))}
            value={a.totalTableSlots}
            onChange={clearOnChange(fieldId("totalTableSlots"), (e) =>
              updateArea(idx, {
                totalTableSlots: Number(e.target.value),
              })
            )}
          />
          <Form.Control.Feedback type="invalid">
            {fieldErrors[fieldId("totalTableSlots")]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={4}>
        <Form.Group
          className={
            isInvalid(fieldId("partySize"))
              ? "table-booking-field-error-group"
              : ""
          }
        >
          <Form.Label>{t("TABLE_BOOKING_MIN_PARTY")}</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={1}
            data-field-id={fieldId("partySize")}
            isInvalid={isInvalid(fieldId("partySize"))}
            value={a.minPartySize}
            onChange={clearOnChange(fieldId("partySize"), (e) =>
              updateArea(idx, {
                minPartySize: Number(e.target.value),
              })
            )}
          />
          <Form.Control.Feedback type="invalid">
            {fieldErrors[fieldId("partySize")]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={4}>
        <Form.Group
          className={
            isInvalid(fieldId("partySize"))
              ? "table-booking-field-error-group"
              : ""
          }
        >
          <Form.Label>{t("TABLE_BOOKING_MAX_PARTY")}</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={1}
            data-field-id={`${fieldId("partySize")}-max`}
            isInvalid={isInvalid(fieldId("partySize"))}
            value={a.maxPartySize}
            onChange={clearOnChange(fieldId("partySize"), (e) =>
              updateArea(idx, {
                maxPartySize: Number(e.target.value),
              })
            )}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 border border-secondary rounded px-2 py-2 mt-1">
          <strong className="small mb-0">{t("TABLE_BOOKING_NESTED_TABLES")}</strong>
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            className="py-0"
            onClick={() => addTable(idx)}
          >
            + {t("TABLE_BOOKING_ADD_TABLE")}
          </Button>
        </div>
        <Form.Text className="d-block mt-1">
          {t("TABLE_BOOKING_NESTED_TABLES_HINT")}
        </Form.Text>
        {hasTables ? (
          <Accordion
            className="mt-2 table-booking-tables-accordion"
            flush
            activeKey={expandedTableAccordion}
            onSelect={(key) => onExpandedTableAccordionChange?.(key)}
          >
            {tables.map((tbl, ti) => (
              <Accordion.Item eventKey={String(ti)} key={`tbl-${idx}-${ti}`}>
                <Accordion.Header>
                  <span className="d-flex align-items-center gap-2 flex-wrap w-100 me-2">
                    <span className="text-truncate">
                      {tbl.label?.trim() ||
                        t("TABLE_BOOKING_TABLE_ROW", { n: ti + 1 })}
                    </span>
                    {focusTableIndex === ti && focusTableIndex != null ? (
                      <Badge bg="info">
                        {t("TABLE_BOOKING_TABLE_EDITING_MAP")}
                      </Badge>
                    ) : null}
                  </span>
                </Accordion.Header>
                <Accordion.Body className="pt-2 pb-3">
                  <Row className="g-2">
                    <Col xs={12} className="d-flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          focusTableIndex === ti
                            ? "primary"
                            : "outline-secondary"
                        }
                        onClick={() =>
                          onFocusTableIndex(
                            focusTableIndex === ti ? null : ti
                          )
                        }
                      >
                        {focusTableIndex === ti
                          ? t("TABLE_BOOKING_TABLE_MAP_EDITING")
                          : t("TABLE_BOOKING_TABLE_PIN_MAP")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-danger"
                        disabled={tables.length <= 0}
                        onClick={() => removeTable(idx, ti)}
                      >
                        {t("TABLE_BOOKING_REMOVE_TABLE")}
                      </Button>
                    </Col>
                    <Col xs={12}>
                      <Form.Group
                        className={
                          isInvalid(tableFieldId(ti, "label"))
                            ? "table-booking-field-error-group"
                            : ""
                        }
                      >
                        <Form.Label>{t("TABLE_BOOKING_TABLE_LABEL")}</Form.Label>
                        <Form.Control
                          size="sm"
                          data-field-id={tableFieldId(ti, "label")}
                          isInvalid={isInvalid(tableFieldId(ti, "label"))}
                          value={tbl.label}
                          onChange={clearOnChange(
                            tableFieldId(ti, "label"),
                            (e) =>
                              updateTable(idx, ti, { label: e.target.value })
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors[tableFieldId(ti, "label")]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>{t("TABLE_BOOKING_TABLE_CODE")}</Form.Label>
                        <Form.Control
                          size="sm"
                          value={tbl.code}
                          onChange={(e) =>
                            updateTable(idx, ti, { code: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small text-muted mb-1">
                        {t("TABLE_BOOKING_TABLE_AVAIL")}
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-1">
                        {["AUTO", "SOLD_OUT", "UNAVAILABLE"].map((k) => {
                          const effectiveAvail = String(
                            tbl.manualAvailability ||
                              a.manualAvailability ||
                              "AUTO"
                          ).toUpperCase();
                          const active = effectiveAvail === k;
                          return (
                            <button
                              key={k}
                              type="button"
                              className={`btn btn-sm ${
                                active ? "btn-studio-primary" : "btn-studio-secondary"
                              }`}
                              onClick={() =>
                                updateTable(idx, ti, {
                                  manualAvailability: k,
                                })
                              }
                            >
                              {manualAvailabilityButtonLabel(k, t)}
                            </button>
                          );
                        })}
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small text-muted mb-1">
                        {t("TABLE_BOOKING_ONLINE_SOLD_OUT")}
                      </Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <CustomToggleButton
                          id={tableFieldId(ti, "onlineSoldOut")}
                          checked={Boolean(
                            toSoldOutFlag(a.onlineSoldOut) ||
                              toSoldOutFlag(tbl.onlineSoldOut)
                          )}
                          disabled={toSoldOutFlag(a.onlineSoldOut)}
                          onCheckChange={(checked) =>
                            requestChannelSoldOutChange(
                              {
                                field: "onlineSoldOut",
                                current: Boolean(
                                  toSoldOutFlag(a.onlineSoldOut) ||
                                    toSoldOutFlag(tbl.onlineSoldOut)
                                ),
                                checked,
                              },
                              (nextChecked) =>
                                updateTable(idx, ti, {
                                  onlineSoldOut: Boolean(nextChecked),
                                })
                            )
                          }
                        />
                      </div>
                      {toSoldOutFlag(a.onlineSoldOut) ? (
                        <Form.Text className="d-block">
                          {t("TABLE_BOOKING_CHANNEL_SOLD_OUT_INHERITED")}
                        </Form.Text>
                      ) : (
                        <Form.Text className="d-block">
                          {t("TABLE_BOOKING_ONLINE_SOLD_OUT_HINT")}
                        </Form.Text>
                      )}
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small text-muted mb-1">
                        {t("TABLE_BOOKING_OFFLINE_SOLD_OUT")}
                      </Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <CustomToggleButton
                          id={tableFieldId(ti, "offlineSoldOut")}
                          checked={Boolean(
                            toSoldOutFlag(a.offlineSoldOut) ||
                              toSoldOutFlag(tbl.offlineSoldOut)
                          )}
                          disabled={toSoldOutFlag(a.offlineSoldOut)}
                          onCheckChange={(checked) =>
                            requestChannelSoldOutChange(
                              {
                                field: "offlineSoldOut",
                                current: Boolean(
                                  toSoldOutFlag(a.offlineSoldOut) ||
                                    toSoldOutFlag(tbl.offlineSoldOut)
                                ),
                                checked,
                              },
                              (nextChecked) =>
                                updateTable(idx, ti, {
                                  offlineSoldOut: Boolean(nextChecked),
                                })
                            )
                          }
                        />
                      </div>
                      {toSoldOutFlag(a.offlineSoldOut) ? (
                        <Form.Text className="d-block">
                          {t("TABLE_BOOKING_CHANNEL_SOLD_OUT_INHERITED")}
                        </Form.Text>
                      ) : (
                        <Form.Text className="d-block">
                          {t("TABLE_BOOKING_OFFLINE_SOLD_OUT_HINT")}
                        </Form.Text>
                      )}
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("TABLE_BOOKING_PRICE_FROM_PERSON")}{" "}
                          <span className="text-muted">
                            ({t("TABLE_BOOKING_OPTIONAL_INHERIT")})
                          </span>
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          step={1}
                          inputMode="numeric"
                          value={tbl.priceFromPerPerson}
                          placeholder={String(a.priceFromPerPerson ?? "")}
                          onWheel={blockWheelChangeOnNumberInput}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              priceFromPerPerson: e.target.value,
                            })
                          }
                          onBlur={(e) =>
                            updateTable(idx, ti, {
                              priceFromPerPerson: e.target.value.trim()
                                ? parsePriceFromPerson(e.target.value)
                                : "",
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("TABLE_BOOKING_CURRENCY")}{" "}
                          <span className="text-muted">
                            ({t("TABLE_BOOKING_OPTIONAL_INHERIT")})
                          </span>
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          value={tbl.currency}
                          placeholder={a.currency || "INR"}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              currency: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          {t("TABLE_BOOKING_PRICE_DISPLAY")}{" "}
                          <span className="text-muted">
                            ({t("TABLE_BOOKING_OPTIONAL_INHERIT")})
                          </span>
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          value={tbl.priceDisplayLabel}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              priceDisplayLabel: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Form.Group>
                        <Form.Label>{t("TABLE_BOOKING_TABLE_SLOTS")}</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          value={tbl.totalTableSlots}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              totalTableSlots: Number(e.target.value) || 1,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Form.Group
                        className={
                          isInvalid(tableFieldId(ti, "partySize"))
                            ? "table-booking-field-error-group"
                            : ""
                        }
                      >
                        <Form.Label>
                          {t("TABLE_BOOKING_MIN_PARTY")}{" "}
                          <span className="text-muted">
                            ({t("TABLE_BOOKING_OPTIONAL_INHERIT")})
                          </span>
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          data-field-id={tableFieldId(ti, "partySize")}
                          isInvalid={isInvalid(tableFieldId(ti, "partySize"))}
                          value={tbl.minPartySize}
                          placeholder={String(a.minPartySize ?? "")}
                          onChange={clearOnChange(
                            tableFieldId(ti, "partySize"),
                            (e) =>
                              updateTable(idx, ti, {
                                minPartySize: e.target.value,
                              })
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors[tableFieldId(ti, "partySize")]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Form.Group
                        className={
                          isInvalid(tableFieldId(ti, "partySize"))
                            ? "table-booking-field-error-group"
                            : ""
                        }
                      >
                        <Form.Label>
                          {t("TABLE_BOOKING_MAX_PARTY")}{" "}
                          <span className="text-muted">
                            ({t("TABLE_BOOKING_OPTIONAL_INHERIT")})
                          </span>
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          data-field-id={`${tableFieldId(ti, "partySize")}-max`}
                          isInvalid={isInvalid(tableFieldId(ti, "partySize"))}
                          value={tbl.maxPartySize}
                          placeholder={String(a.maxPartySize ?? "")}
                          onChange={clearOnChange(
                            tableFieldId(ti, "partySize"),
                            (e) =>
                              updateTable(idx, ti, {
                                maxPartySize: e.target.value,
                              })
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>{t("DESCRIPTION")}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={tbl.description}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              description: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>{t("TABLE_BOOKING_MEAL_SUMMARY")}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={tbl.mealSummary}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              mealSummary: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>{t("TABLE_BOOKING_PANORAMA_URL")}</Form.Label>
                        <Form.Control
                          size="sm"
                          value={tbl.panoramaUrl}
                          onChange={(e) =>
                            updateTable(idx, ti, {
                              panoramaUrl: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      {isInvalid(tableFieldId(ti, "hotspot")) ? (
                        <Alert
                          variant="danger"
                          className="py-2 small mb-2 table-booking-field-error-group"
                          data-field-id={tableFieldId(ti, "hotspot")}
                        >
                          {fieldErrors[tableFieldId(ti, "hotspot")]}
                        </Alert>
                      ) : null}
                      <Form.Text className="small text-muted mb-0">
                        {t("TABLE_BOOKING_TABLE_HOTSPOT_HINT")}
                      </Form.Text>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : null}
      </Col>

      <Col xs={12}>
        <div className="border border-secondary rounded p-2 mb-2">
          <Form.Label className="small fw-semibold mb-1 d-block">
            {t("TABLE_BOOKING_OUTLINE_SECTION")}
          </Form.Label>
          <p className="small text-muted mb-2">
            {t("TABLE_BOOKING_OUTLINE_HELP")}
          </p>
          {outlinePointCount > 0 ? (
            <Badge bg="secondary" className="me-2 mb-2">
              {t("TABLE_BOOKING_OUTLINE_POINTS", { n: outlinePointCount })}
            </Badge>
          ) : (
            <Badge bg="warning" text="dark" className="me-2 mb-2">
              {t("TABLE_BOOKING_OUTLINE_NONE")}
            </Badge>
          )}
          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={outlineDrawMode ? "primary" : "outline-primary"}
              disabled={!hasSceneImage}
              onClick={onStartOutlineDraw}
            >
              {outlineDrawMode
                ? t("TABLE_BOOKING_OUTLINE_DRAWING")
                : t("TABLE_BOOKING_OUTLINE_DRAW")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              disabled={!hasSceneImage}
              onClick={onExpandMap}
            >
              {t("TABLE_BOOKING_EXPAND_MAP")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-danger"
              disabled={outlinePointCount < 1}
              onClick={onClearOutline}
            >
              {t("TABLE_BOOKING_OUTLINE_CLEAR")}
            </Button>
          </div>
          {outlineDrawMode ? (
            <p className="small text-info mb-0 mt-2">
              {t("TABLE_BOOKING_OUTLINE_DRAW_HINT")}
            </p>
          ) : null}
        </div>
      </Col>

      <Col xs={12}>
        <button
          type="button"
          className="btn btn-link btn-sm px-0 py-0 mb-1"
          onClick={() => setShowHotspotNumbers((v) => !v)}
        >
          {showHotspotNumbers
            ? t("TABLE_BOOKING_HIDE_HOTSPOT_NUMBERS")
            : t("TABLE_BOOKING_SHOW_HOTSPOT_NUMBERS")}
        </button>
        <Collapse in={showHotspotNumbers}>
          <div>
            <p className="small text-muted mb-1">
              {focusTableIndex != null && hasTables
                ? t("TABLE_BOOKING_HOTSPOT_NUMBERS_TABLE_MODE")
                : t("TABLE_BOOKING_HOTSPOT_NUMBERS_ZONE_MODE")}
            </p>
            <Row className="g-2">
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>X (0–1)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.001"
                    min={0}
                    max={1}
                    value={
                      focusTableIndex != null && tables[focusTableIndex]
                        ? tables[focusTableIndex].hotspot?.x ?? 0
                        : a.hotspot?.x ?? 0
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (focusTableIndex != null && tables[focusTableIndex]) {
                        updateTable(idx, focusTableIndex, {
                          hotspot: {
                            ...tables[focusTableIndex].hotspot,
                            x: v,
                          },
                        });
                      } else {
                        onHotspotChange(idx, { x: v });
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>Y (0–1)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.001"
                    min={0}
                    max={1}
                    value={
                      focusTableIndex != null && tables[focusTableIndex]
                        ? tables[focusTableIndex].hotspot?.y ?? 0
                        : a.hotspot?.y ?? 0
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (focusTableIndex != null && tables[focusTableIndex]) {
                        updateTable(idx, focusTableIndex, {
                          hotspot: {
                            ...tables[focusTableIndex].hotspot,
                            y: v,
                          },
                        });
                      } else {
                        onHotspotChange(idx, { y: v });
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>{t("TABLE_BOOKING_HOTSPOT_RADIUS")}</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.005"
                    min={0.005}
                    max={1}
                    disabled={focusTableIndex != null}
                    value={
                      focusTableIndex != null && tables[focusTableIndex]
                        ? tables[focusTableIndex].hotspot?.radius ?? 0.02
                        : a.hotspot?.radius ?? 0.04
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (focusTableIndex != null && tables[focusTableIndex]) {
                        updateTable(idx, focusTableIndex, {
                          hotspot: {
                            ...tables[focusTableIndex].hotspot,
                            radius: v,
                          },
                        });
                      } else {
                        onHotspotChange(idx, { radius: v });
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Collapse>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{t("TABLE_BOOKING_PANORAMA_URL")}</Form.Label>
          <Form.Control
            size="sm"
            value={a.panoramaUrl}
            onChange={(e) =>
              updateArea(idx, { panoramaUrl: e.target.value })
            }
          />
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{t("DESCRIPTION")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={a.description}
            onChange={(e) =>
              updateArea(idx, { description: e.target.value })
            }
          />
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{t("TABLE_BOOKING_MEAL_SUMMARY")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={a.mealSummary}
            onChange={(e) =>
              updateArea(idx, { mealSummary: e.target.value })
            }
          />
        </Form.Group>
      </Col>
    </Row>
    <ConfirmModal
      modalTitle={t("TABLE_BOOKING_CONFIRM_CHANNEL_TITLE")}
      modalDescription={t("TABLE_BOOKING_CONFIRM_CHANNEL_CHANGE", {
        channel: channelConfirmLabel,
        state: channelConfirmState,
      })}
      isUnderline={false}
      showModal={Boolean(pendingChannelChange)}
      leftBtnTitle={t("YES_CONFIRM")}
      isModelIcon={true}
      iconColor="mdi mdi-help-circle-outline"
      iconCricle="iconCricleCancel"
      leftBtnClass="ok_Btn"
      isFirstButton={true}
      isSecondButton={true}
      rightBtnClass="borderButton"
      rightBtnTitle={t("NO_CANCEL")}
      onProcced={confirmChannelChange}
      rightButtonClick={closeChannelConfirm}
      onHide={closeChannelConfirm}
    />
    </>
  );
};

export default AreaInspectorFields;
